import React from 'react';
import { Heading, Link, Table } from "arag-ui-lib";
import accounting from 'accounting';
import { CANADA_COUNTRY, COUNTRY, CURRENCY } from "../../helpers/variables";
import {
    CLINICAL_NEGLIGENCE_MN,
    COSTS_SUMMARY_PAY_PREMIUM_TEXT,
    HEADING_3,
    LEFT,
    PREMIUM_PAYABLE_EXCL_TAX_HEADER,
    SIZE_LG,
    TAX,
    TERTIARY,
    TOTAL_PREMIUM_PAYABLE_INCL_TAX_HEADER,
} from '../../helpers/constants';
import ModalWrapper from '../../helpers/modalWrapper';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import '../../style/table.css';

const isCanada = COUNTRY == CANADA_COUNTRY;

const CostsSummary = (props) => {
    const { costsSummary, costsSummaryModal, toggleCostsSummaryModal, selectedApp, advanceDisbursementsEnabled } = props;

    if (!costsSummary) return null;

    const taxText = CONTENT_STRINGS.TAX_INCLUSIVE_RATE_MESSAGE + costsSummary.taxPercentage + "%";

    const premiumColumns = [
        { propertyName: 'netPremium', header: PREMIUM_PAYABLE_EXCL_TAX_HEADER },
        { propertyName: 'premiumTax', header: TAX },
        { propertyName: 'premium', header: TOTAL_PREMIUM_PAYABLE_INCL_TAX_HEADER },
    ];

    const isStagedPremium = !!costsSummary?.stageName;

    let tableColumns;
    if (isStagedPremium) {
        tableColumns = [
            { propertyName: 'stage', header: CONTENT_STRINGS.STAGE },
            ...premiumColumns,
        ];
    } else {
        tableColumns = [
            ...(costsSummary?.damagesReceived ? [{ propertyName: 'damages', header: CONTENT_STRINGS.DAMAGES_RECEIVED }] : []),
            ...premiumColumns,
        ];
        if (selectedApp && selectedApp.caseTypeName == CLINICAL_NEGLIGENCE_MN) {
            tableColumns.push(
                { propertyName: 'recoverable', header: CONTENT_STRINGS.INSURANCE_PREMIUM_RECOVERABLE_FROM_OPPONENT },
                { propertyName: 'payable', header: CONTENT_STRINGS.INSURANCE_PREMIUM_PAYABLE_FROM_DAMAGES }
            );
        }
    }

    const tableData = [{
        damages: costsSummary?.damagesReceived,
        stage: costsSummary?.stageName,
        premium: `${accounting.formatMoney(costsSummary?.insurancePremium, CURRENCY, 2)} `,
        netPremium: `${accounting.formatMoney(costsSummary?.netPremium, CURRENCY, 2)} `,
        premiumTax: `${accounting.formatMoney(costsSummary?.netPremiumTax, CURRENCY, 2)} `,
        recoverable: `${accounting.formatMoney(costsSummary?.recoverable, CURRENCY, 2)} `,
        payable: `${accounting.formatMoney(costsSummary?.payable, CURRENCY, 2)} `,
    }];

    const adsNotSettled = [
        { propertyName: 'type', header: CONTENT_STRINGS.DISBURSEMENT_TYPE },
        { propertyName: 'amount', header: CONTENT_STRINGS.AMOUNT, formatter: (row) => accounting.formatMoney(row.amount, CURRENCY, 2) },
        { propertyName: 'status', header: CONTENT_STRINGS.STATUS },
        { propertyName: 'settledAmount', header: CONTENT_STRINGS.SETTLED_AMOUNT, formatter: (row) => accounting.formatMoney((row.totalAmount - row.unsettledAmount), CURRENCY, 2) },
        { propertyName: 'createdOn', header: CONTENT_STRINGS.DATE_CREATED },
        { propertyName: 'paidOn', header: CONTENT_STRINGS.DATE_PAID },
        { propertyName: 'reference', header: CONTENT_STRINGS.PAYMENT_REFERENCE },
    ];

    return (
        <ModalWrapper
            isOpen={costsSummaryModal}
            onClose={toggleCostsSummaryModal}
            title={CONTENT_STRINGS.COSTS_SUMMARY}
            size={SIZE_LG}
            modalTestId={'costs-summary-modal'}
            buttonTestId={'costs-summary-close'}
            footer={
                <Link
                    label={CONTENT_STRINGS.CLOSE}
                    variant={TERTIARY}
                    onClick={toggleCostsSummaryModal}
                    arrowDirection={LEFT}
                    linkTestId={"costs-summary-close-link"}
                />
            }
        >
            <Heading
                variant={HEADING_3}
                title={COSTS_SUMMARY_PAY_PREMIUM_TEXT}
                headingTestId={"costs-summary-payment-text"}
            />
            <div className='table-container'>
                <Table
                    columns={tableColumns}
                    data={tableData}
                    striped
                    tableWrapperTestId="costs-summary-premium-table"
                />
            </div>
            {
                advanceDisbursementsEnabled
                ?
                    costsSummary?.paidPartSettledADs?.length > 0
                    ?
                        <div className='table-container'>
                            <Table
                                columns={adsNotSettled}
                                data={costsSummary?.paidPartSettledADs.map(ad => ({
                                    type: ad.description,
                                    amount: ad.totalAmount,
                                    status: ad.status,
                                    totalAmount: ad.totalAmount,
                                    unsettledAmount: ad.unsettledAmount,
                                    createdOn: ad.creationDate ? (new Date(ad.creationDate?.split('T')[0])?.toLocaleDateString('en-GB')) : '',
                                    paidOn: ad.paymentDate ? (new Date(ad.paymentDate?.split('T')[0])?.toLocaleDateString('en-GB')) : '',
                                    reference: ad.paymentInstructionId,
                                }))}
                                striped
                                tableWrapperTestId="costs-summary-disbursements-table"
                            />
                        </div>
                    :
                        isCanada
                        ?
                            null
                        :
                            <Heading
                                variant={HEADING_3}
                                title={CONTENT_STRINGS.NO_DISBURSEMENT_PAID}
                                headingTestId={"costs-summary-no-disbursements-text"}
                            />
                :
                    null
            }
            {
                isCanada 
                ? 
                    null 
                : 
                    <Heading
                        variant={HEADING_3} 
                        title={taxText}
                        headingTestId={"costs-summary-ipt-text"}
                    />
            }
        </ModalWrapper>
    );
};

export default CostsSummary;
